import axios from 'axios';
import {
	ENDPOINT_URL,
	GET_AAA_SERVER_LIST,
} from '../../../constants/commonConstants';
import {replaceResolvedEntities} from '../../core/resolvedEntityStore';
import {
	decodeAgentResponse,
	optionalText,
	rankLookupCandidates,
	responseRows,
} from '../../core/resolutionUtils';

/**
 * Return an AAA Server row identifier.
 * @param {object} item Canvas AAA Server row
 * @return {*|null} identifier
 */
function aaaServerId(item) {
	return item.radius_server_id ?? item.id ?? null;
}

/**
 * Normalize a Canvas AAA Server row without exposing server configuration.
 * @param {object} item Canvas AAA Server row
 * @return {object|null} agent candidate
 */
function normalizeAaaServer(item) {
	const id = aaaServerId(item);
	const name = item.radius_server_name ?? item.name;
	if (id == null || !name) return null;
	const safeId = String(id).trim();
	const safeName = String(name).trim();
	if (!safeId || !safeName) return null;
	return {
		entity_type: 'aaa_server',
		entity_id: safeId,
		name: safeName,
		site_id: null,
		site_name: null,
		security_type: null,
		status: optionalText(item.status),
		serial_no: null,
	};
}

/**
 * Resolve visible AAA Server choices from the authenticated Canvas API.
 * Empty queries return the first bounded page for initial suggestions.
 * @param {object} params resolver parameters
 * @return {Promise<{candidates: object[]}>} normalized candidates
 */
export async function resolveAaaServers(params = {}) {
	const query = String(params.query || '').trim();
	const requestedLimit = Number(params.limit);
	const limit = Number.isInteger(requestedLimit)
		? Math.min(Math.max(requestedLimit, 1), 25)
		: 5;
	const search = query
		? `&search=${encodeURIComponent(query)}`
		: '';
	const url = `${ENDPOINT_URL}${GET_AAA_SERVER_LIST}?limit=${limit}` +
		`&offset=0${search}`;
	const {data} = await axios.get(url);
	const rows = responseRows(decodeAgentResponse(data) || {});
	// Retained so the page can act on the row the agent names. The
	// table it lives in may still be loading when that command lands.
	replaceResolvedEntities('aaa_server', rows, (item) => item.radius_server_id ?? item.id);
	const normalized = rows.map(normalizeAaaServer).filter(Boolean);
	return {
		candidates: query
			? rankLookupCandidates(normalized, query, limit)
			: normalized.slice(0, limit),
	};
}
